/**
 * Subscription plan limits for vehicles and drivers
 * Based on the subscription plans API feature metadata
 */

export const SUBSCRIPTION_LIMITS = {
  solo: {
    maxVehicles: 1,
    maxDrivers: 1,
  },
  fleet: {
    maxVehicles: 10,
    maxDrivers: 10,
  },
  enterprise: {
    maxVehicles: 100000,
    maxDrivers: 100000,
  },
} as const;

export type PlanSlug = keyof typeof SUBSCRIPTION_LIMITS;

/**
 * Get the limits for a specific subscription plan
 * @param planSlug - The subscription plan slug (solo, fleet, enterprise)
 * @returns The limits object for the plan, or solo limits as fallback
 */
export function getPlanLimits(planSlug: string) {
  const slug = planSlug.toLowerCase() as PlanSlug;
  return SUBSCRIPTION_LIMITS[slug] || SUBSCRIPTION_LIMITS.solo;
}

/**
 * Check if the plan should show count/limit indicators
 * Only Fleet plan shows the limit indicator (e.g., "My Vehicles (1/10)")
 * @param planSlug - The subscription plan slug
 * @returns true if limits should be displayed
 */
export function shouldShowLimitIndicator(planSlug: string): boolean {
  return planSlug.toLowerCase() === "fleet";
}

/**
 * Temporarily disabled plans
 * Solo is disabled until the Solo app is ready for deployment
 */
const DISABLED_PLANS: Record<string, string> = {
  solo: "Coming Soon",
};

/**
 * Check if a plan is temporarily disabled
 * @param planSlug - The subscription plan slug
 * @returns Object with disabled status and reason if disabled
 */
export function getDisabledPlanInfo(planSlug: string): { disabled: boolean; reason?: string } {
  const slug = planSlug.toLowerCase();
  const reason = DISABLED_PLANS[slug];

  if (reason) {
    return { disabled: true, reason };
  }

  return { disabled: false };
}
